import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, XCircle, ShieldAlert } from 'lucide-react'
import { useApproveProposal, useProposalDetail, useRejectProposal } from '@/hooks/useProposals'
import { cn } from '@/lib/utils'

const statusStyles: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  executed: 'bg-blue-100 text-blue-800',
  expired: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-800',
  draft: 'bg-gray-100 text-gray-600',
}

function formatDate(value?: string) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

export function ProposalDetailPage() {
  const { proposalId } = useParams()
  const navigate = useNavigate()

  const { data, isLoading, error } = useProposalDetail(proposalId ?? null)
  const approveMutation = useApproveProposal()
  const rejectMutation = useRejectProposal()

  const proposal = data?.proposal
  const events = data?.events ?? []

  const canApprove = proposal?.status === 'pending'

  const actionLabel = useMemo(() => {
    if (!proposal) return ''
    if (proposal.requiresApproval) return '承認が必要'
    return '自動実行'
  }, [proposal])

  const handleApprove = async () => {
    if (!proposalId) return
    await approveMutation.mutateAsync({ id: proposalId })
  }

  const handleReject = async () => {
    if (!proposalId) return
    const reason = window.prompt('却下理由を入力してください（任意）') || undefined
    await rejectMutation.mutateAsync({ id: proposalId, reason })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
        >
          <ArrowLeft className="mr-2 inline h-4 w-4" />
          戻る
        </button>
        <div>
          <h1 className="text-2xl font-bold">提案詳細</h1>
          <p className="text-muted-foreground">
            根拠と履歴を確認し、必要に応じて承認します。
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="text-sm text-muted-foreground">読み込み中...</div>
      )}
      {error && (
        <div className="text-sm text-red-600">詳細の取得に失敗しました。</div>
      )}

      {proposal && (
        <div className="space-y-6">
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">
                  {proposal.title ?? '無題の提案'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {proposal.summary ?? 'サマリーが未設定です'}
                </p>
              </div>
              <span
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium',
                  statusStyles[proposal.status]
                )}
              >
                {proposal.status}
              </span>
            </div>

            <div className="mt-4 grid gap-3 text-sm text-muted-foreground md:grid-cols-4">
              <div>優先度: {proposal.priority}</div>
              <div>スコープ: {proposal.actionScope}</div>
              <div>リスク: {proposal.riskLevel}</div>
              <div>作成日: {formatDate(proposal.createdAt)}</div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <ShieldAlert className="h-4 w-4 text-primary" />
              {actionLabel}
            </div>

            {canApprove && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                  承認
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                  className="flex items-center gap-2 rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4 text-red-500" />
                  却下
                </button>
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h3 className="mb-3 font-semibold">提案本文</h3>
            <div className="whitespace-pre-wrap text-sm text-muted-foreground">
              {proposal.body}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h3 className="mb-3 font-semibold">Evidence Pack</h3>
            <p className="text-sm text-muted-foreground">
              {proposal.evidence.reasoning}
            </p>
            <div className="mt-4 space-y-3">
              {proposal.evidence.sources.map((source) => (
                <div key={source.documentId} className="rounded-md border p-3 text-xs">
                  <div className="font-medium text-foreground">
                    {source.sourceType ?? 'source'} / {source.documentId}
                  </div>
                  {source.excerpt && (
                    <div className="mt-1 text-muted-foreground">{source.excerpt}</div>
                  )}
                  {source.createdAt && (
                    <div className="mt-1 text-muted-foreground">
                      {formatDate(source.createdAt)}
                    </div>
                  )}
                </div>
              ))}
              {proposal.evidence.sources.length === 0 && (
                <div className="text-sm text-muted-foreground">根拠はまだ登録されていません。</div>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <h3 className="mb-3 font-semibold">承認履歴</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              {events.map((event) => (
                <div key={event.id} className="rounded-md border px-3 py-2">
                  <div className="font-medium text-foreground">{event.action}</div>
                  <div className="text-xs text-muted-foreground">
                    {event.actorId ?? 'system'} ・ {formatDate(event.occurredAt)}
                  </div>
                </div>
              ))}
              {events.length === 0 && (
                <div className="text-sm text-muted-foreground">イベントはありません。</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
